import { computed, watch, Ref } from "vue";
import { useI18n } from "vue-i18n";
import { getOptionGroups, getPlanList } from '@@@/planCatalog/planCatalog';

import { usePlanSelectionStore } from "@/features/planSelection/stores/usePlanSelectionStore";
import { useFormStore } from "@/features/blueprint/stores/useFormStore";
import { useDomainSelectionStore } from "@/features/domain-checker-input/stores/useDomainSelectionStore";
import { evaluateRecommendationRules, generatePackageSpecificReasons } from "@/features/planSelection/composables/useRecommendation";

import type { PlanOptionGroup } from "@/features/planSelection/models/PlanOptionGroup";
import { PlanOption } from "@/features/planSelection/models/PlanOption";
import { UiOption } from "../types/UiOptions";

/**
 * UI-ready ViewModel that enriches each PlanOptionGroup
 * with selection flags, recommendation logic, and triggered rules.
 */
export function usePlanSelectionViewModel(billingInterval: Ref<"monthly" | "yearly">) {
    const { t } = useI18n();
    const planStore = usePlanSelectionStore();
    const formStore = useFormStore();
    const domainStore = useDomainSelectionStore();

    const rawGroups = getOptionGroups();
    const allPlans = getPlanList();

    // Evaluate recommendation rules against current answers (reactive)
    // If URL preselected plans are provided, use those instead of automatic recommendations
    const recommendationResults = computed(() => {
        if (planStore.urlPreselectedPlans.length > 0) {
            // Generate descriptions for URL preselected plans using the same logic
            const { triggeredDescriptions } = evaluateRecommendationRules(formStore.answers);
            // Create a custom recommendation result for URL plans
            return {
                selectedPlanIds: planStore.urlPreselectedPlans,
                triggeredDescriptions: generatePackageSpecificReasons(formStore.answers, planStore.urlPreselectedPlans)
            };
        }
        return evaluateRecommendationRules(formStore.answers);
    });
    
    // Update auto-selected plans when recommendations change
    watch(
        () => recommendationResults.value.selectedPlanIds,
        (selectedPlanIds) => {
            planStore.autoSelectedPlans = selectedPlanIds;
        },
        { immediate: true }
    );
    function enrichPlanOption(base: PlanOption, group: PlanOptionGroup): UiOption {
        return Object.assign(base, {
            isSelected: planStore.selectedPlans.includes(base.id),
            isRecommended: planStore.autoSelectedPlans.includes(base.id),
            isPopular: group.popularIds?.includes(base.id) ?? false,
            alwaysSelected: group.alwaysSelectedIds?.includes(base.id) ?? false,
        });
    }


    const groups = computed<Array<PlanOptionGroup & { options: UiOption[] }>>(() => {
        return rawGroups.map((group) => {
            const visibleOptions = group.options
                .filter((opt) => !group.hasHidden?.includes(opt.id))
                .map((opt) => {
                    const found = allPlans.find((p) => p.id === opt.id);
                    return found ? enrichPlanOption(found, group) : null;
                })
                .filter((p): p is UiOption => p !== null);

            return {
                ...group,
                options: visibleOptions,
            };
        });
    });

    function toggleOption(groupId: string, optionId: string) {
        const group = rawGroups.find((g) => g.id === groupId)!;

        const isSelected = planStore.selectedPlans.includes(optionId);
        const isRequired = group.required ?? false;

        // Handle mutual exclusions within the group
        if (group.mutualExclusionGroups && group.multiSelect) {
            // Find which mutual exclusion group (if any) contains this option
            const mutualExclusionGroup = group.mutualExclusionGroups.find(exclusionGroup => 
                exclusionGroup.includes(optionId)
            );

            if (mutualExclusionGroup && !isSelected) {
                // If we're selecting this option, deselect all other options in the mutual exclusion group
                mutualExclusionGroup
                    .filter(id => id !== optionId && planStore.selectedPlans.includes(id))
                    .forEach(id => planStore.togglePlanSelection(id));
            } else if (isSelected && isRequired && mutualExclusionGroup) {
                // Don't allow deselecting if this is in a required group and no other option from the exclusion group is selected
                const hasOtherSelectedInGroup = mutualExclusionGroup.some(id => 
                    id !== optionId && planStore.selectedPlans.includes(id)
                );
                if (!hasOtherSelectedInGroup) {
                    return; // Prevent deselection
                }
            }
        } else if (!group.multiSelect) {
            // Legacy behavior for non-multiSelect groups
            if (isSelected) {
                if (!isRequired) {
                    planStore.togglePlanSelection(optionId);
                }
                return;
            }

            group.options
                .map((o) => o.id)
                .filter((id) => planStore.selectedPlans.includes(id))
                .forEach((id) => planStore.togglePlanSelection(id));
        }

        planStore.togglePlanSelection(optionId);
    }

    const selectedPlanOptions = computed<PlanOption[]>(() => {
        return planStore.selectedPlans
            .map(id => allPlans.find(p => p.id === id))
            .filter((p): p is PlanOption => p !== undefined)
            .sort((a, b) => {
                const priority = (p: PlanOption) =>
                    p.isOneTime ? 0 : p.isMonthly ? 1 : p.isYearly ? 2 : 3
                const diff = priority(a) - priority(b)
                return diff !== 0 ? diff : a.price - b.price
            });
    });



    const signupLink = computed(() => {
        const plans = planStore.selectedPlans.join(",");
        const interval = billingInterval.value;
        return `/checkout?plans=${encodeURIComponent(plans)}&interval=${encodeURIComponent(interval)}`;
    });

    const triggeredRecommendationEntries = computed(() =>
        recommendationResults.value.triggeredDescriptions.map((key) => ({
            key,
            text: t(key),
        }))
    );

    return {
        groups,
        toggleOption,
        selectedPlanOptions,
        signupLink,
        triggeredRecommendationEntries,
    };
}
